import { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Users, Phone, MapPin, Car, Clock, Loader2, Truck, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@flote/ui';
import type { DriverStatus, DeliveryTaskStatus } from '@flote/types/delivery';
import type { UpdateDriverRequest } from '@flote/contracts/http';
import { deliveryTsr } from '../../api';
import { DriverFormModal } from '../../components/drivers';
import { getErrorMessage } from '../../lib/error-utils';

const DRIVER_STATUS_BADGES: Record<DriverStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string }> = {
  off_duty: { label: 'Off Duty', variant: 'outline', className: 'bg-slate-100 text-slate-500 border-slate-200' },
  idle: { label: 'Available', variant: 'default', className: 'bg-green-100 text-green-700 border-green-200 hover:bg-green-100' },
  active: { label: 'Busy', variant: 'default', className: 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100' },
};

const TASK_STATUS_BADGES: Record<DeliveryTaskStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  pending: { label: 'Pending', variant: 'outline' },
  assigned: { label: 'Assigned', variant: 'secondary' },
  in_transit: { label: 'In Transit', variant: 'default' },
  arrived: { label: 'Arrived', variant: 'default' },
  completed: { label: 'Completed', variant: 'secondary' },
  failed: { label: 'Failed', variant: 'destructive' },
};

export function DriverDetailPage() {
  const { driverId } = useParams<{ driverId: string }>();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch driver details
  const { data: driverResponse, isLoading: driverLoading, refetch: refetchDriver } = deliveryTsr.adminDrivers.getById.useQuery({
    queryKey: ['driver', driverId],
    queryData: { params: { driverId: driverId! } },
  });
  const driver = driverResponse?.status === 200 ? driverResponse.body : null;

  // Fetch driver's tasks
  const { data: tasksResponse, isLoading: tasksLoading } = deliveryTsr.adminDrivers.getTasks.useQuery({
    queryKey: ['driver-tasks', driverId],
    queryData: {
      params: { driverId: driverId! },
      query: { limit: 20 },
    },
  });
  const tasks = tasksResponse?.status === 200 ? tasksResponse.body : [];

  // Fetch driver's team IDs
  const { data: teamsResponse, refetch: refetchTeams } = deliveryTsr.adminDrivers.getTeams.useQuery({
    queryKey: ['driver-teams', driverId],
    queryData: { params: { driverId: driverId! } },
  });
  const driverTeamIds = teamsResponse?.status === 200 ? teamsResponse.body : [];

  // Fetch all teams for the edit form
  const { data: allTeamsResponse } = deliveryTsr.adminTeams.list.useQuery({
    queryKey: ['teams-list'],
  });
  const teams = allTeamsResponse?.status === 200 ? allTeamsResponse.body : [];

  // Fetch vehicles for the edit form
  const { data: vehiclesResponse } = deliveryTsr.adminVehicles.list.useQuery({
    queryKey: ['vehicles-list-for-driver-edit'],
    queryData: { query: { limit: 100 } },
  });
  const vehicles = vehiclesResponse?.status === 200 ? vehiclesResponse.body : [];

  // Mutation for updating driver
  const updateDriverMutation = deliveryTsr.adminDrivers.update.useMutation();

  const handleUpdateDriver = useCallback(async (data: UpdateDriverRequest) => {
    if (!driverId) return;

    setIsUpdating(true);
    try {
      const response = await updateDriverMutation.mutateAsync({
        params: { driverId },
        body: data,
      });

      if (response.status === 200) {
        toast.success('Driver updated successfully');
        setIsEditModalOpen(false);
        refetchDriver();
        refetchTeams();
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to update driver');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update driver'));
      console.error('Update driver error:', error);
    } finally {
      setIsUpdating(false);
    }
  }, [driverId, updateDriverMutation, refetchDriver, refetchTeams]);

  if (driverLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="space-y-6">
        <Link to="/drivers" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Drivers
        </Link>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Users className="mb-4 h-12 w-12 text-muted-foreground/50" />
            <h3 className="mb-2 text-lg font-medium">Driver not found</h3>
            <p className="text-sm text-muted-foreground">
              The driver you're looking for doesn't exist or has been removed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusBadge = DRIVER_STATUS_BADGES[driver.status];

  return (
    <div className="space-y-6">
      {/* Header with back link */}
      <div className="flex items-center justify-between">
        <Link to="/drivers" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Drivers
        </Link>
      </div>

      {/* Driver Profile Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            {/* Avatar */}
            {driver.photoUrl ? (
              <img
                src={driver.photoUrl}
                alt={driver.name}
                className="h-24 w-24 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-muted">
                <Users className="h-12 w-12 text-muted-foreground" />
              </div>
            )}

            {/* Info */}
            <div className="flex-1 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold">{driver.name}</h1>
                  {driver.displayName && driver.displayName !== driver.name && (
                    <p className="text-muted-foreground">{driver.displayName}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={statusBadge.variant} className={statusBadge.className}>{statusBadge.label}</Badge>
                  <Badge
                    variant={driver.onDuty ? 'default' : 'outline'}
                    className={driver.onDuty ? 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100' : 'bg-slate-100 text-slate-500 border-slate-200'}
                  >
                    {driver.onDuty ? 'On Duty' : 'Off Duty'}
                  </Badge>
                  <Button variant="outline" size="sm" onClick={() => setIsEditModalOpen(true)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {/* Phone */}
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{driver.phone}</span>
                </div>

                {/* Location */}
                {driver.location && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {driver.location.lat.toFixed(4)}, {driver.location.lng.toFixed(4)}
                    </span>
                  </div>
                )}

                {/* Vehicle */}
                {driver.vehicle && (
                  <div className="flex items-center gap-2 text-sm">
                    <Car className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {driver.vehicle.description || driver.vehicle.type}
                      {driver.vehicle.licensePlate && ` (${driver.vehicle.licensePlate})`}
                    </span>
                  </div>
                )}

                {/* Last Seen */}
                {driver.lastSeenAt && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>Last seen: {new Date(driver.lastSeenAt).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Driver's Tasks */}
      <Card>
        <CardHeader>
          <CardTitle>Assigned Tasks</CardTitle>
        </CardHeader>
        <CardContent>
          {tasksLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Truck className="mb-4 h-12 w-12 text-muted-foreground/50" />
              <h3 className="mb-2 text-lg font-medium">No tasks assigned</h3>
              <p className="text-sm text-muted-foreground">
                This driver doesn't have any tasks assigned yet.
              </p>
              <Button asChild className="mt-4">
                <Link to="/tasks">View All Tasks</Link>
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tracking ID</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>ETA</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task) => {
                  const taskStatusBadge = TASK_STATUS_BADGES[task.status] ?? { label: task.status, variant: 'outline' as const };
                  return (
                    <TableRow key={task.id}>
                      <TableCell className="font-mono text-sm">{task.trackingId}</TableCell>
                      <TableCell>{task.recipientName}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {task.address.city}, {task.address.state}
                      </TableCell>
                      <TableCell>
                        <Badge variant={taskStatusBadge.variant}>{taskStatusBadge.label}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {task.estimatedArrival
                          ? new Date(task.estimatedArrival).toLocaleTimeString()
                          : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Driver Modal */}
      <DriverFormModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        onSubmit={handleUpdateDriver}
        isProcessing={isUpdating}
        teams={teams}
        vehicles={vehicles}
        driver={driver}
        driverTeamIds={driverTeamIds}
      />
    </div>
  );
}
